import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Grid,
  Typography,
  Paper,
  Checkbox,
  Box,
  Chip,
  useMediaQuery,
  Skeleton,
} from "@mui/material";
import CommonButton from "../../../Components/InputComponents/CommonButton";
import SelectInput from "../../../Components/InputComponents/SelectInput";
import {
  errorNotify,
  successNotify,
} from "../../../Components/ToastComponent/ToastNotification";
import { axiosAPI } from "../../../Components/AxiosComponents/Axios";
import { decryptId } from "../../../Components/CryptoComponent";

const EditDocumentProcedure = ({ maxwidth = "none" }) => {
  const { pid, cid } = useParams();
  const decryptePId = decryptId(decodeURIComponent(pid));
  const decrypteCId = decryptId(decodeURIComponent(cid));

  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 1220px)");
  const [isDisabled, setIsDisabled] = useState(false);
  const [formData, setFormData] = useState({});

  const [selectedDocuments, setSelectedDocuments] = useState([]);

  const [errors, setErrors] = useState({});

  const [countries, setCountries] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [documentStages, setDocumentStages] = useState([]);

  const [loader, setLoader] = useState({
    country: true,
    profession: true,
    documents: true,
  });

  const getCountries = async () => {
    try {
      setLoader((prev) => ({ ...prev, country: true }));
      const res = await axiosAPI(`/RecruitmentCountry/list`);
      setCountries(res?.data?.data || []);
    } catch (err) {
      console.error(err);
      errorNotify("Failed to load countries.");
    } finally {
      setLoader((prev) => ({ ...prev, country: false }));
    }
  };

  const getProfessions = async () => {
    try {
      setLoader((prev) => ({ ...prev, profession: true }));
      const res = await axiosAPI(`/Profession/active/list`);
      setProfessions(res?.data?.data || []);
    } catch (err) {
      console.error(err);
      errorNotify("Failed to load professions.");
    } finally {
      setLoader((prev) => ({ ...prev, profession: false }));
    }
  };

  const getDocumentStages = async () => {
    try {
      setLoader((prev) => ({ ...prev, documents: true }));
      const res = await axiosAPI.get(`/DocChecklist/active/list`);
      setDocumentStages(res?.data?.data || []);
    } catch (err) {
      console.error("Active API failed, retrying without 'active'", err);
      errorNotify("Failed to load Documents.");
      try {
        const resFallback = await axiosAPI.get(`/DocChecklist/list`);
        setDocumentStages(resFallback?.data?.data || []);
      } catch (fallbackErr) {
        console.error("Fallback API also failed", fallbackErr);
      }
    } finally {
      setLoader((prev) => ({ ...prev, documents: false }));
    }
  };

  const handleChange = (name) => (event) => {
    setFormData({
      ...formData,
      [name]: event.target.value,
    });
  };

  const handleToggle = (doc) => {
    setSelectedDocuments((prev) =>
      prev.some((d) => d.DC_Id === doc.DC_Id)
        ? prev.filter((d) => d.DC_Id !== doc.DC_Id)
        : [...prev, doc]
    );
  };

  const validateForm = () => {
    const formErrors = {};

    if (!formData.country) {
      formErrors.country = "Country is required.";
    }
    if (!formData.profession) {
      formErrors.profession = "Profession is required.";
    }
    if (selectedDocuments.length === 0) {
      formErrors.selectedDocuments = "Please select at least one document.";
    }

    setErrors(formErrors);

    if (Object.keys(formErrors).length > 0) {
      Object.values(formErrors).forEach((error) => {
        errorNotify(error);
      });
      return false;
    }

    return true;
  };

  const getData = () => {
    axiosAPI
      ?.post(`/RecruitmentProcedureMapping/listbycountryProfession`, {
        professionId: decryptePId,
        countryId: decrypteCId,
      })
      .then((res) => {
        if (res?.data?.success) {
          const mapped = res?.data?.data?.Documents?.map((doc) => ({
            DC_Id: doc.DocumentID,
            DC_Name: doc.DocumentName,
            DC_Status: 1,
          }));

          if (mapped) {
            setSelectedDocuments(mapped);
            setFormData({
              country: res?.data?.data?.CountryID,
              profession: res?.data?.data?.ProfessionID,
            });
          }
        } else {
          errorNotify(res?.data?.message);
          setLoader(true);
        }
      })
      .catch((err) => {
        errorNotify("Failed to load data.");
        setLoader(true);
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsDisabled(true);
    setLoader(true);

    const payload = {
      CountryID: formData.country,
      ProfessionID: formData.profession,
      DocumentIDs: selectedDocuments.map((doc) => doc.DC_Id),
    };

    axiosAPI
      .post(`/DocChecklist/document-mapping/edit`, payload)
      .then((res) => {
        if (res?.data?.success) {
          successNotify(res?.data?.message);
          navigate(`/DocumentProcedure`);
        } else {
          errorNotify(res?.data?.message);
        }
      })
      .catch((err) => {
        console.error("Error submitting form:", err);
        errorNotify("Something went wrong");
      })
      .finally(() => {
        setIsDisabled(false);
        setLoader(false);
      });
  };

  useEffect(() => {
    getCountries();
    getProfessions();
    getDocumentStages();
    getData();
  }, []);

  return (
    <Paper
      sx={{
        maxWidth: `100vw`,
        padding: 2,
        margin: "auto",
        boxShadow: "none",
        overflowX: "auto",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: 700, marginBottom: 3 }}>
        Edit Document Checklist Mapping
      </Typography>

      <Grid
        container
        sx={{
          border: "1px solid lightgrey",
          width: "100%",
          margin: "10px auto",
          borderRadius: "10px",
        }}
        p={2}
      >
        {/* Country Dropdown */}
        <Grid item xs={12} sm={6} p={2}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            Country <span style={{ color: "red" }}>*</span>
          </Typography>

          <SelectInput
            placeholder="Choose Country"
            name="country"
            options={countries.map((c) => ({
              label: c?.RC_CountryName,
              value: c?.RC_Id,
            }))}
            value={formData.country}
            onChange={handleChange("country")}
            isDisabled={true}
            error={!!errors.country}
            loading={loader?.country}
          />
        </Grid>

        {/* Profession Dropdown */}
        <Grid item xs={12} sm={6} p={2}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            Profession <span style={{ color: "red" }}>*</span>
          </Typography>
          <SelectInput
            placeholder="Choose Profession"
            name="profession"
            options={professions.map((p) => ({
              label: p?.PI_Name,
              value: p?.PI_Id,
            }))}
            value={formData.profession}
            onChange={handleChange("profession")}
            isDisabled={true}
            error={!!errors.profession}
            loading={loader?.profession}
          />
        </Grid>

        {/* Document Checklist */}
        <Grid item xs={12} p={2}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            Document Checklist
          </Typography>
          <Paper
            sx={{ maxHeight: 500, overflowY: "auto", p: 1, boxShadow: "none" }}
          >
            <Grid container spacing={2}>
              {loader?.documents ? (
                <>
                  {Array.from({ length: 16 }).map((_, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
                      <Skeleton
                        key={index}
                        variant="rounded"
                        height={60}
                        sx={{ margin: "10px auto" }}
                      />
                    </Grid>
                  ))}
                </>
              ) : (
                <>
                  {documentStages.map((doc) => {
                    const isChecked = selectedDocuments.some(
                      (d) => d.DC_Id === doc.DC_Id
                    );

                    return (
                      <Grid item xs={12} sm={6} lg={4} key={doc.DC_Id}>
                        <Box
                          sx={{
                            border: isChecked
                              ? "2px solid #006CB8"
                              : "1px solid #ccc",
                            borderRadius: 1,
                            p: 1,
                            transition: "border-color 0.3s",
                          }}
                        >
                          <Chip
                            label={
                              <Box display="flex" alignItems="center">
                                <Checkbox
                                  checked={isChecked}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggle(doc);
                                  }}
                                  sx={{ mr: 1 }}
                                />
                                <Typography variant="body2">
                                  {doc.DC_Name}
                                </Typography>
                              </Box>
                            }
                            sx={{
                              width: "100%",
                              justifyContent: "flex-start",
                              "&:hover": {
                                backgroundColor: "#EBF2FF",
                              },
                              background: "transparent",
                            }}
                            onClick={() => handleToggle(doc)}
                          />
                        </Box>
                      </Grid>
                    );
                  })}
                </>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* Action Buttons */}
        <Grid container spacing={2} justifyContent="flex-end">
          <Grid item>
            <CommonButton
              variant="outlined"
              customColor="#B0BEC5"
              onClick={() => navigate(`/DocumentProcedure`)}
            >
              Cancel
            </CommonButton>
          </Grid>
          <Grid item>
            <CommonButton
              onClick={handleSubmit}
              variant="contained"
              customColor="#007BFF"
              disabled={isDisabled}
            >
              {isDisabled ? "Saving..." : "Save"}
            </CommonButton>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default EditDocumentProcedure;
